import { InvoiceDiscountLevelDisplayId } from "../../../domain/enums/displayId.enum"
import { InvoiceStatus, PaymentStatus } from "../../../domain/enums/status.enum"
import { PaymentStatusCount } from "../../../domain/Payments/schema"
import {
  getData,
  buildURLWithQueryParams,
  buildUrl,
  postDataFlexibleTyped,
} from "../apiService"
import { posthog } from "../../../posthogInstance"
import { FeatureFlag } from "../../../domain/enums/featureFlags"

export interface InvoiceDto {
  id?: string
  sessionId: string
  adpSessionId: number
  caseNumber: number
  providerId: string
  providerName: string
  practiceId: string
  practiceName: string
  billRate?: number
  isUnderSupervision: boolean
  sessionDateTime: string
  invoiceDateTime: string
  invoiceStatusDisplayId: string // TODO: enum-ize this
  discountLevelDisplayId: InvoiceDiscountLevelDisplayId
  adjustmentAmount?: number
  adjustmentNote?: string
  createdDateTime: string
  updatedDateTime: string
}

export interface PaginatedPaymentDetails {
  totalCount: number
  items: PaymentDetail[]
}

export interface PaymentCSVExport {
  caseNumber: number
  sessionId: string
  adpSessionId: number
  providerName: string
  practiceName: string
  billRate: number | null
  isUnderSupervision: boolean
  sessionDateTime: string
  invoiceDateTime: string
  createdDateTime: string
  updatedDateTime: string
  invoiceStatusDisplayId: string
  discountLevelDisplayId: string
  grossPayAmount: number | null
  adjustmentAmount?: number | null
  adjustmentNote?: string | null
  discountPayAmount: number | null
  checkPaymentNumber: string | null
  paymentDate: string | null
  paymentUpdatedDateTime: string | null
  paymentCreatedDateTime: string | null
  paymentStatusDisplayId: string | null
  runGlCode: string | null
  runDate: string | null
  runTime: string | null
  runStatusDisplayId: string | null
}

export interface PaymentDetail {
  caseNumber: number
  adpSessionId: string
  sessionId: string
  sessionDate: string
  sessionTime: string
  completedDate: string
  providerId: string
  providerName: string
  practiceId: string
  practiceName: string
  invoiceId?: string
  originalAmount: number
  adjustedAmount: number
  adjustmentNote?: string
  status?: InvoiceStatus | PaymentStatus
  checkNumber?: string
  amountPaid?: number
  paymentDate?: string
  updatedDate?: string
  invoiceDate?: string
  referralId?: string
  referralCode?: string
}

export interface CaseViewPaymentDetail extends PaymentDetail {
  referralId?: string
  referralCode?: string
}

export interface CasePaymentExportDto {
  caseNumber: number
  sessionId: string
  adpSessionId: number | null
  sessionDate: string
  sessionTime: string
  completedDate: string
  providerId: string
  providerName: string
  practiceId: string
  practiceName: string
  invoiceId: string | null
  originalAmount: number | null
  adjustedAmount: number | null
  adjustmentNote: string | null
  invoiceDate: string | null
  invoiceCreatedBy: string | null
  invoiceUpdatedDateTime: string
  invoiceUpdatedBy: string | null
  status: string | null
  checkNumber: string | null
  amountPaid: number | null
  paymentDate: string | null
  paymentCreatedBy: string | null
  paymentUpdatedDateTime: string | null
  paymentUpdatedBy: string | null
  paymentCreatedDateTime: string | null
}

export type PaymentDetailsStatusCount = {
  statusCounts: PaymentStatusCount
}

export interface NextPaymentAndInvoicingDateDto {
  nextInvoicingDate: string
  nextPaymentDate: string
}

export interface CasePaymentSummaryDto {
  paymentsIssued: number
  paymentsOutstanding: number
}

const apiRoutes = {
  get: {
    INVOICES_BY_ID: "/payment/invoices",
    NEXT_PAYMENT_AND_INVOICING_DATE: "/payment/nextpaymentandinvoicingdate",
    PAYMENT_DETAILS_BY_PRACTICE_ID_ROUTE:
      "/payment/practice/:practiceId/paymentdetails",
    PAYMENT_DETAILS_STATUS_COUNTS_ROUTE:
      "/payment/practice/:practiceId/paymentdetails/statuscounts",
    PAYMENT_CSV_EXPORT: "/payment/practice/:practiceId/paymentdetails/export",
    CASE_PAYMENT_STATUS_COUNTS_ROUTE: "/payment/case/:caseNumber/statuscounts",
    PAYMENT_DETAILS_BY_CASE_NUMBER: "/payment/case/:caseNumber/paymentdetails",
    PAYMENT_DETAILS_BY_CASE_NUMBER_EXPORT:
      "/payment/case/:caseNumber/paymentdetails/export",
  },
  post: {
    INVOICE: "/payment/invoices",
  },
}

const getInvoicesById = async ({
  invoiceId,
}: {
  invoiceId: string
}): Promise<InvoiceDto[]> => {
  return getData(
    buildURLWithQueryParams(
      apiRoutes.get.INVOICES_BY_ID,
      {},
      { InvoiceId: invoiceId },
    ),
  )
}

const getNextPaymentAndInvoicingDate = async () => {
  return getData<NextPaymentAndInvoicingDateDto>(
    buildUrl(apiRoutes.get.NEXT_PAYMENT_AND_INVOICING_DATE),
  )
}

const getPaginatedPaymentDetailsByPracticeId = async ({
  practiceId,
  body,
}: {
  practiceId: string
  body: {
    filter?: {
      providerId: string | null
      paymentOrInvoiceStatusDisplayIds: string[] | null
      providerIds?: string[] | null
    }
    count: number
    offset: number
  }
}) => {
  return getData<PaginatedPaymentDetails>(
    buildUrl(apiRoutes.get.PAYMENT_DETAILS_BY_PRACTICE_ID_ROUTE, {
      practiceId,
    }),
    true,
    body,
  )
}

const getPaymentDetailsByPracticeId = async ({
  practiceId,
}: {
  practiceId: string
}) => {
  return getData<PaymentDetail[]>(
    buildUrl(apiRoutes.get.PAYMENT_DETAILS_BY_PRACTICE_ID_ROUTE, {
      practiceId,
    }),
  )
}

const getPaymentDetailsExport = async ({
  practiceId,
  providerId,
}: {
  practiceId: string
  providerId?: string
}) => {
  const queryParams = providerId ? { providerId } : undefined
  return getData<PaymentCSVExport[]>(
    buildURLWithQueryParams(
      apiRoutes.get.PAYMENT_CSV_EXPORT,
      { practiceId },
      queryParams,
    ),
  )
}

const getPaymentDetailsStatusCountByPracticeId = async ({
  practiceId,
  providerId,
}: {
  practiceId: string
  providerId?: string
}) => {
  const urlParamRecord: Record<string, string> = { practiceId }
  const queryParamRecord: Record<string, string> = {}
  if (providerId) {
    queryParamRecord["providerId"] = providerId
  }
  return getData<PaymentDetailsStatusCount["statusCounts"]>(
    buildURLWithQueryParams(
      apiRoutes.get.PAYMENT_DETAILS_STATUS_COUNTS_ROUTE,
      urlParamRecord,
      queryParamRecord,
    ),
  )
}

const getPaymentDetailsByCaseNumber = async ({
  caseNumber,
}: {
  caseNumber: string
}): Promise<PaymentDetail[]> => {
  return getData<PaymentDetail[]>(
    buildUrl(apiRoutes.get.PAYMENT_DETAILS_BY_CASE_NUMBER, { caseNumber }),
  )
}

const getPaymentDetailsByCaseNumberExport = async ({
  caseNumber,
}: {
  caseNumber: string
}): Promise<CasePaymentExportDto[]> => {
  const version = posthog.isFeatureEnabled(
    FeatureFlag.IsCaseViewExportUserName19866Enabled,
  )
    ? "2.0"
    : "1.0"

  return getData<CasePaymentExportDto[]>(
    buildURLWithQueryParams(
      apiRoutes.get.PAYMENT_DETAILS_BY_CASE_NUMBER_EXPORT,
      { caseNumber },
      { "api-version": version },
    ),
  )
}

const getCasePaymentStatusCounts = async (
  caseNumber: string,
): Promise<CasePaymentSummaryDto> => {
  return getData<CasePaymentSummaryDto>(
    buildUrl(apiRoutes.get.CASE_PAYMENT_STATUS_COUNTS_ROUTE, { caseNumber }),
  )
}

const postInvoice = async (payload: unknown) => {
  return postDataFlexibleTyped(apiRoutes.post.INVOICE, payload)
}

export const paymentsApi = {
  get: {
    INVOICES_BY_ID: getInvoicesById,
    NEXT_PAYMENT_AND_INVOICING_DATE: getNextPaymentAndInvoicingDate,
    PAYMENT_CSV_EXPORT: getPaymentDetailsExport,
    PAYMENT_DETAILS_BY_PRACTICE_ID: getPaymentDetailsByPracticeId,
    PAYMENT_DETAILS_STATUS_COUNTS_BY_PRACTICE_ID:
      getPaymentDetailsStatusCountByPracticeId,
    CASE_PAYMENT_STATUS_COUNTS: getCasePaymentStatusCounts,
    PAYMENT_DETAILS_BY_CASE_NUMBER: getPaymentDetailsByCaseNumber,
    PAYMENT_DETAILS_BY_CASE_NUMBER_EXPORT: getPaymentDetailsByCaseNumberExport,
  },
  post: {
    INVOICE: postInvoice,
    PAGINATED_PAYMENT_DETAILS_BY_PRACTICE_ID:
      getPaginatedPaymentDetailsByPracticeId,
  },
}
